import { useCallback, useEffect, useRef, useState } from 'react';
import { getHumanTasks } from '../api';

const POLL_INTERVAL_MS = 60000;

/**
 * Tracks how many human tasks are open (the "Needs You" inbox badge).
 * Polls slowly; call `refresh` after resolving a task so the badge updates
 * immediately.
 */
export function useHumanTaskCount(workspaceId: number) {
  const [openCount, setOpenCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await getHumanTasks(workspaceId, { status: 'open', limit: 1 });
      setOpenCount(res.open_count ?? 0);
    } catch {
      // Silently ignore network errors; keep the last known count.
    }
  }, [workspaceId]);

  useEffect(() => {
    const tick = async () => {
      if (!document.hidden) await refresh();
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    void tick();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refresh]);

  return { openCount, refresh };
}
